/**
 * Same-origin FastAPI calls; Caddy (or the Vite dev proxy) maps /api/* to the
 * API. Response types come from the generated OpenAPI types (`api-types.ts`,
 * regenerate with `npm run generate:api`).
 */
import type { components } from './api-types';

export type MeResponse = components['schemas']['MeResponse'];
export type CatalogResponse = components['schemas']['CatalogResponse'];
export type CatalogEntry = components['schemas']['CatalogEntry'];
export type OverviewResponse = components['schemas']['OverviewResponse'];
export type NewsResponse = components['schemas']['NewsResponse'];
export type TrendResponse = components['schemas']['TrendResponse'];
export type EntitlementResponse = components['schemas']['EntitlementResponse'];
export type CheckoutResponse = components['schemas']['CheckoutResponse'];
export type PortalResponse = components['schemas']['PortalResponse'];
export type Plan = components['schemas']['Plan'];
export type WatchlistsResponse = components['schemas']['WatchlistsResponse'];
export type Watchlist = components['schemas']['WatchlistModel'];

export class ApiError extends Error {
  readonly status: number;
  /** The API's machine-readable `detail` string, when it sent one. */
  readonly detail: string | null;

  constructor(status: number, detail: string | null = null) {
    super(`API request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

async function errorDetail(response: Response): Promise<string | null> {
  try {
    const body = (await response.json()) as { detail?: unknown };
    return typeof body.detail === 'string' ? body.detail : null;
  } catch {
    return null;
  }
}

async function request<T>(
  path: string,
  accessToken: string,
  method = 'GET',
  body?: unknown,
): Promise<T> {
  const init: RequestInit = { method, headers: { Authorization: `Bearer ${accessToken}` } };
  if (body !== undefined) {
    init.headers = { ...init.headers, 'Content-Type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const response = await fetch(`/api${path}`, init);
  if (!response.ok) {
    throw new ApiError(response.status, await errorDetail(response));
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export function fetchMe(accessToken: string): Promise<MeResponse> {
  return request('/v1/me', accessToken);
}

export function fetchCatalog(accessToken: string): Promise<CatalogResponse> {
  return request('/v1/tickers', accessToken);
}

export function fetchOverview(accessToken: string, symbol: string): Promise<OverviewResponse> {
  return request(`/v1/tickers/${encodeURIComponent(symbol)}/overview`, accessToken);
}

export function fetchNews(accessToken: string, symbol: string, limit = 20): Promise<NewsResponse> {
  return request(`/v1/tickers/${encodeURIComponent(symbol)}/news?limit=${limit}`, accessToken);
}

export function fetchTrend(accessToken: string, symbol: string, days = 30): Promise<TrendResponse> {
  return request(`/v1/tickers/${encodeURIComponent(symbol)}/trend?days=${days}`, accessToken);
}

export function fetchEntitlement(accessToken: string): Promise<EntitlementResponse> {
  return request('/v1/billing/entitlement', accessToken);
}

/** Returns the Stripe Checkout URL to redirect the browser to. */
export function startCheckout(accessToken: string): Promise<CheckoutResponse> {
  return request('/v1/billing/checkout', accessToken, 'POST');
}

/** Returns the Stripe customer portal URL to redirect the browser to. */
export function openBillingPortal(accessToken: string): Promise<PortalResponse> {
  return request('/v1/billing/portal', accessToken, 'POST');
}

export function fetchWatchlists(accessToken: string): Promise<WatchlistsResponse> {
  return request('/v1/watchlists', accessToken);
}

export function createWatchlist(accessToken: string, name: string): Promise<Watchlist> {
  return request('/v1/watchlists', accessToken, 'POST', { name });
}

export function renameWatchlist(accessToken: string, id: number, name: string): Promise<Watchlist> {
  return request(`/v1/watchlists/${id}`, accessToken, 'PATCH', { name });
}

export function deleteWatchlist(accessToken: string, id: number): Promise<void> {
  return request(`/v1/watchlists/${id}`, accessToken, 'DELETE');
}

export function addToWatchlist(
  accessToken: string,
  id: number,
  symbol: string,
): Promise<Watchlist> {
  return request(`/v1/watchlists/${id}/tickers/${encodeURIComponent(symbol)}`, accessToken, 'PUT');
}

export function removeFromWatchlist(
  accessToken: string,
  id: number,
  symbol: string,
): Promise<Watchlist> {
  return request(
    `/v1/watchlists/${id}/tickers/${encodeURIComponent(symbol)}`,
    accessToken,
    'DELETE',
  );
}
